import { useOrganizationAdminUserList } from "@/api/services/admin/organization";
import { TOrganizationUserDto } from "@/api/services/admin/organization/dtos/get-organization-detail.dto";
import { useEnum } from "@/api/services/global/enum";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { SimplePagination } from "@/components/elements/SimplePagination";
import { Table } from "@/components/elements/Table";
import { useFillViewportHeight } from "@/hooks/useFillViewportHeight";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { dayjs } from "@/utils/dayjs";
import { findLabelByValue } from "@/utils/object";
import { Stack } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useEffect, useMemo, useState } from "react";

type AdminUserTableProps = {
  onTotalChange?: (total: number) => void;
};

export const AdminUserTable: FC<AdminUserTableProps> = ({ onTotalChange }) => {
  const [page, setPage] = useState(1);

  const { ref: tableAreaRef, maxHeight: tableMaxHeight } = useFillViewportHeight<HTMLDivElement>({ bottomGap: 176 });

  const { data, isLoading, isFetching } = useOrganizationAdminUserList({ page });
  // Tab 2 now shares the /users endpoint (mirror rows), so role/status carry EUserRole/EUserStatus values.
  const { data: adminUserRoles } = useEnum("EUserRole");
  const { data: adminUserStatuses } = useEnum("EUserStatus");

  const items = data?.data ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    if (pagination?.total != null) onTotalChange?.(pagination.total);
  }, [pagination?.total, onTotalChange]);

  const columns: MRT_ColumnDef<TOrganizationUserDto>[] = useMemo(
    () => [
      {
        accessorKey: "fullName",
        header: "ユーザー名",
        Cell: ({ row }) => <EllipsisText title={row.original?.fullName ?? EMPTY_STR.TEXT} variant="body14" maxWidth={168} />,
        size: 180,
      },
      {
        accessorKey: "email",
        header: "メールアドレス",
        Cell: ({ row }) => <EllipsisText title={row.original?.email ?? EMPTY_STR.TEXT} variant="body14" maxWidth={200} />,
        size: 220,
      },
      {
        accessorKey: "role",
        header: "権限",
        Cell: ({ row }) => (row.original?.role ? findLabelByValue(adminUserRoles, row.original.role) : EMPTY_STR.TEXT),
        size: 74,
      },
      {
        accessorKey: "status",
        header: "ステータス",
        Cell: ({ row }) => (row.original?.status ? findLabelByValue(adminUserStatuses, row.original.status) : EMPTY_STR.TEXT),
        size: 104,
      },
      {
        accessorKey: "lastLoginAt",
        header: "最終ログイン",
        Cell: ({ row }) => (row.original?.lastLoginAt ? dayjs(row.original.lastLoginAt).format(DATE_FORMAT) : EMPTY_STR.DATE),
        size: 116,
      },
      {
        accessorKey: "createdAt",
        header: "登録日",
        Cell: ({ row }) => (row.original?.createdAt ? dayjs(row.original.createdAt).format(DATE_FORMAT) : EMPTY_STR.DATE),
        size: 106,
      },
    ],
    [adminUserRoles, adminUserStatuses],
  );

  const { table } = useTable({
    columns,
    rows: items,
    layoutMode: "grid",
  });

  return (
    <Stack ref={tableAreaRef} gap={2}>
      <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight={tableMaxHeight} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SimplePagination page={page} totalPage={pagination?.totalPage} onChange={setPage} />
      </Stack>
    </Stack>
  );
};
